'use client';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import { ShowcaseCopyContext, translateShowcaseNode } from '../translation';
import { ProductionArtifact } from './production-primitives';
import { ProductionIcon } from './deliverable-previews';
import {
  EstimateBadge,
  SourcingOption,
  SourcingTable,
  CostRange,
  Kp,
} from './collection-review';
import { w } from './workflow-facts';
import { J, tf, ef } from './sell-market-animation';
import { ShowcaseIcon } from './scene-primitives';
import { FileTypeIcon } from './deliverable-files';
import { referenceImages } from './reference-brands';
export function SourcingPlan({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <ProductionArtifact
      icon={'tag'}
      title={translateShowcaseNode(
        showcaseCopy,
        'Box Tote Sourcing and Costing'
      )}
      view={'Plan'}
      footer={
        <React.Fragment>
          <span
            className={'text-ink-3 flex min-w-0 items-center gap-2 text-[13px]'}
          >
            <ProductionIcon name={'info'} className={'size-4 shrink-0'} />
            <span className={'text-pretty'}>
              {translateShowcaseNode(
                showcaseCopy,
                'Planning estimates, not quotes. No suppliers were contacted.'
              )}
            </span>
          </span>
          <EstimateBadge className={'ml-auto max-sm:hidden'} />
        </React.Fragment>
      }
    >
      <div
        className={
          'grid gap-5 p-3 sm:p-5 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] md:gap-6'
        }
      >
        <div className={'flex min-w-0 flex-col gap-4'}>
          <div>
            <p className={'label'}>
              {translateShowcaseNode(showcaseCopy, 'Production routes')}
            </p>
            <SourcingOption t={e} />
          </div>
          <div>
            <p className={'label mb-1.5'}>
              {translateShowcaseNode(showcaseCopy, 'Regions')}
            </p>
            <SourcingTable t={e} />
          </div>
        </div>
        <div className={'flex min-w-0 flex-col gap-4'}>
          <div>
            <p className={'label mb-1.5'}>
              {translateShowcaseNode(showcaseCopy, w.product)}
              {translateShowcaseNode(showcaseCopy, ' \xB7 ')}
              {translateShowcaseNode(showcaseCopy, w.size)}
            </p>
            <CostRange t={e} />
          </div>
          <div
            {...J(e, Kp)}
            className={
              'pd-pop flex flex-col gap-1.5 rounded-[10px] border border-[var(--line)] p-3'
            }
          >
            <span
              className={
                'text-ink flex items-center gap-1.5 text-[13px] font-medium'
              }
            >
              <ProductionIcon
                name={'circleCheck'}
                on={e >= Kp}
                className={'size-4'}
              />
              {translateShowcaseNode(showcaseCopy, 'Recommendation')}
            </span>
            <span className={'text-ink text-[13px] leading-[1.5] text-pretty'}>
              {translateShowcaseNode(showcaseCopy, w.recommendation)}
            </span>
            <span
              className={'text-ink-3 flex items-center gap-1.5 text-[12.5px]'}
            >
              <ShowcaseIcon name={'clock'} className={'size-3.5 shrink-0'} />
              {translateShowcaseNode(showcaseCopy, w.delivery)}
            </span>
          </div>
        </div>
      </div>
    </ProductionArtifact>
  );
}
export const im = [
  {
    label: 'Route',
    value: 'Full-package, Florence / Tuscany',
  },
  {
    label: 'From',
    value: 'Casa Orvieto, Milan',
  },
  {
    label: 'Product',
    value: `Box tote, ${w.size}`,
  },
  {
    label: 'Quantity',
    value: `${w.quantity} units`,
  },
  {
    label: 'Delivery',
    value: 'Milan receipt by 15 July 2027',
  },
  {
    label: 'Pack',
    value: 'Tech pack v2 \xB7 BOM v2',
  },
];
export const am =
  'Casa Orvieto requests a full-package quotation for the box tote below, quoted against the attached tech pack and bill of materials.';
export const om = 900;
export const sm = [
  'Packed unit price at 300 units',
  'Minimum order and colour minimums',
  'Production and sample lead time',
  'Payment terms and validity',
];
export const cm = [
  {
    name: 'tote-tech-pack-v2.pdf',
    kind: 'pdf',
  },
  {
    name: 'tote-bom.sheet',
    kind: 'spreadsheet',
  },
  {
    name: 'colorways.pdf',
    kind: 'pdf',
  },
];
export const lm = 5e3;
export const um = 3300;
export const dm = 260;
export function SupplierRFQ({ t: e }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let t = tf(am, e, om, 80),
    n = t.length > 0 && t.length < 131,
    r = ef(e, 2500, 160, sm.length),
    i = ef(e, 2900, 140, cm.length),
    a = ef(e, um, dm, w.conflicts.length);
  return (
    <ProductionArtifact
      icon={'mail'}
      title={translateShowcaseNode(showcaseCopy, 'Supplier RFQ')}
      view={'Document'}
      footer={
        <React.Fragment>
          <span
            {...J(e, lm)}
            className={
              'pd-in text-ink flex min-w-0 items-center gap-2 text-[13px]'
            }
          >
            <ShowcaseIcon
              name={'clock'}
              className={'text-ink-3 size-4 shrink-0'}
            />
            <span className={'text-pretty'}>
              {translateShowcaseNode(
                showcaseCopy,
                'Ready to send after your review. Nothing has been sent.'
              )}
            </span>
          </span>
          <span
            {...J(e, lm)}
            className={'pd-pop ml-auto flex shrink-0 items-center gap-2'}
          >
            <span className={'btn btn-soft btn-sm max-sm:hidden'}>
              {translateShowcaseNode(showcaseCopy, 'Edit')}
            </span>
            <span className={'btn btn-ink btn-sm'}>
              {translateShowcaseNode(showcaseCopy, 'Review draft')}
            </span>
          </span>
        </React.Fragment>
      }
    >
      <div className={'p-3 sm:p-5'}>
        <article
          className={
            'pd-sheet mx-auto flex max-w-[620px] flex-col gap-4 rounded-[10px] p-4 shadow-[inset_0_0_0_1px_var(--line)] sm:p-6'
          }
        >
          <header
            {...J(e, 100)}
            className={'pd-in flex items-start justify-between gap-3'}
          >
            <div className={'min-w-0'}>
              <p
                className={
                  'text-ink text-[18px] leading-6 font-semibold tracking-[-0.015em]'
                }
              >
                {translateShowcaseNode(showcaseCopy, 'Request for Quotation')}
              </p>
              <p className={'text-ink-3 mt-0.5 font-mono text-[12px]'}>
                {translateShowcaseNode(showcaseCopy, 'COR-TOTE-01 \xB7 draft')}
              </p>
            </div>
            <EstimateBadge />
          </header>
          <dl className={'grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2'}>
            {translateShowcaseNode(
              showcaseCopy,
              im.map((t, n) => (
                <div
                  key={t.label}
                  {...J(e, 300 + n * 90)}
                  className={'pd-in flex min-w-0 gap-3 text-[13px] leading-5'}
                >
                  <dt className={'text-ink-4 w-16 shrink-0'}>
                    {translateShowcaseNode(showcaseCopy, t.label)}
                  </dt>
                  <dd className={'text-ink m-0 min-w-0 truncate'}>
                    {translateShowcaseNode(showcaseCopy, t.value)}
                  </dd>
                </div>
              ))
            )}
          </dl>
          <p
            className={
              'text-ink-2 min-h-[42px] border-t border-[var(--line)] pt-3 text-[13.5px] leading-[1.55] text-pretty'
            }
          >
            {translateShowcaseNode(showcaseCopy, t)}
            {translateShowcaseNode(
              showcaseCopy,
              n ? <span className={'pd-caret'} /> : null
            )}
          </p>
          <p
            {...J(e, 2300)}
            className={
              'pd-in text-ink-2 flex gap-2 rounded-[8px] bg-[var(--tint)] px-3 py-2 text-[12.5px] leading-[1.5]'
            }
          >
            <span className={'text-ink-4 shrink-0'}>
              {translateShowcaseNode(showcaseCopy, 'Material')}
            </span>
            <span>{translateShowcaseNode(showcaseCopy, w.material)}</span>
          </p>
          <div className={'grid gap-3 sm:grid-cols-[1fr_auto]'}>
            <ul className={'flex flex-col gap-1.5'}>
              {translateShowcaseNode(
                showcaseCopy,
                sm.map((e, t) => (
                  <li
                    key={e}
                    data-on={String(r > t)}
                    className={
                      'pd-in text-ink-2 flex items-center gap-2 text-[13px]'
                    }
                  >
                    <ShowcaseIcon
                      name={'check'}
                      className={'text-ink-3 size-3.5 shrink-0'}
                      strokeWidth={2}
                    />
                    {translateShowcaseNode(showcaseCopy, e)}
                  </li>
                ))
              )}
            </ul>
            <ul className={'flex flex-col gap-1.5 sm:items-end'}>
              {translateShowcaseNode(
                showcaseCopy,
                cm.map((e, t) => (
                  <li
                    key={e.name}
                    data-on={String(i > t)}
                    className={
                      'pd-pop text-ink-2 flex h-7 items-center gap-1.5 rounded-md bg-[var(--tint)] pr-2.5 pl-1.5 font-mono text-[12px]'
                    }
                  >
                    <FileTypeIcon
                      kind={e.kind}
                      className={'h-[18px] w-4'}
                      detail={!1}
                    />
                    {translateShowcaseNode(showcaseCopy, e.name)}
                  </li>
                ))
              )}
            </ul>
          </div>
          <section
            {...J(e, 3100)}
            className={
              'pd-in flex flex-col gap-2 border-t border-[var(--line)] pt-3'
            }
          >
            <p className={'flex items-center gap-2 text-[13px]'}>
              <span className={'text-ink font-semibold'}>
                {translateShowcaseNode(showcaseCopy, 'Resolve before sending')}
              </span>
              <span className={'num text-ink-4'}>
                {translateShowcaseNode(showcaseCopy, w.conflicts.length)}
                {translateShowcaseNode(showcaseCopy, ' spec conflicts')}
              </span>
            </p>
            <ul className={'grid gap-1.5 sm:grid-cols-2'}>
              {translateShowcaseNode(
                showcaseCopy,
                w.conflicts.map((e, t) => (
                  <li
                    key={e}
                    data-on={String(a > t)}
                    className={
                      'pd-pop flex items-center gap-2 rounded-[8px] bg-[var(--pd-amber-wash)] px-2.5 py-2 text-[12.5px] leading-[1.4]'
                    }
                  >
                    <span className={'shrink-0 text-[var(--pd-amber)]'}>
                      <ProductionIcon name={'flag'} className={'size-3.5'} />
                    </span>
                    <span className={'text-ink-2'}>
                      {translateShowcaseNode(showcaseCopy, e)}
                    </span>
                  </li>
                ))
              )}
            </ul>
          </section>
        </article>
      </div>
    </ProductionArtifact>
  );
}
export const pm = referenceImages.orvieto.hero;
export const mm = pm.width;
export const hm = pm.height;
